/* eslint-disable quote-props */
import { loadSprinklrSDK } from './sprinklr-loader';
import { SPRINKLR_CONFIG } from '../config/sprinklr-config';
import { logger } from '../../shared/utils/logger';
import { getMcvisID, getSvocId } from '../../shared/utils/window-utils';

export const SPR_CHAT_EVENTS = {
  MESSAGE_RECEIVED: '@chat/MESSAGE_RECEIVED',
  MESSAGE_UPDATED: '@chat/MESSAGE_UPDATED',
  CONVERSATIONS_UPDATED: '@conversation/CONVERSATIONS_UPDATED',
  USER_RESOLVED: '@user/RESOLVED',
  AGENT_START_TYPING: '@user/AGENT_START_TYPING',
  AGENT_STOP_TYPING: '@user/AGENT_STOP_TYPING',
  EXTERNAL_EVENT_SEND: '@conversation/EXTERNAL_EVENT_SEND',
};

let chatInstance = null;
let isReady = false;

/**
 * Loads the Sprinklr SDK script, then initialises the SDK via `.init()`.
 * In MOCK_MODE an in-memory fake instance is used instead.
 *
 * @returns {Promise<object>} Resolves with the chat SDK instance.
 */
export async function initializeSprinklrChat() {
  if (chatInstance && isReady) return chatInstance;

  if (window !== undefined && window?.sprinklr?.chat && window?.sprinklr?.chat.loaded) {
    chatInstance = window.sprinklr.chat;
    isReady = true;
    return chatInstance;
  }

  // ---- Real SDK path ----
  // Read once and reused below: the script URL and init() must name the same app.
  const appId = SPRINKLR_CONFIG.APP_ID;

  // 1. Load the script: <script src="${cdnHost}/sdk/${env}/${appId}">
  logger.log('[Sprinklr] Loading SDK script…');
  await loadSprinklrSDK(
    SPRINKLR_CONFIG.CDN_HOST,
    SPRINKLR_CONFIG.ENVIRONMENT,
    appId,
  );

  // 2. After loading, SDK lives at window.sprinklr.chat
  const chatSdk = window.sprinklr?.chat;
  if (!chatSdk || typeof chatSdk.init !== 'function') {
    throw new Error(
      'Sprinklr SDK not found on window.sprinklr.chat after script load',
    );
  }

  // 3. Initialise the SDK
  const initParams = {
    appId,
    environment: SPRINKLR_CONFIG.ENVIRONMENT,
    locale: SPRINKLR_CONFIG.LOCALE,
  };

  logger.log('[Sprinklr] Initializing SDK with params…', initParams);
  try {
    await chatSdk.init(initParams);
  } catch (err) {
    if (err?.message === 'ALREADY_INITIATED') {
      logger.log('[Sprinklr] SDK already initiated — reusing existing instance');
    } else {
      throw err;
    }
  }
  chatInstance = chatSdk;
  isReady = true;
  logger.log('[Sprinklr] Chat SDK initialized successfully');
  await chatSdk.getConversations({ size: 5 });
  return chatInstance;
}

/**
 * Returns the initialised chat SDK instance.
 * Throws if the SDK has not been initialized.
 */
export function getChatInstance() {
  if (!chatInstance || !isReady) {
    throw new Error(
      'Sprinklr chat is not initialized yet. Call initializeSprinklrChat() first.',
    );
  }
  return chatInstance;
}

/**
 * Whether the Sprinklr chat SDK has been initialized.
 */
export function isSprinklrReady() {
  return isReady;
}

/**
 * Tears down the current chat instance and resets module state.
 * Clears the global window reference so the SDK can be garbage-collected.
 */
export function destroySprinklrChat() {
  chatInstance = null;
  isReady = false;

  if (window.sprinklr) {
    window.sprinklr = null;
  }
}

async function getUserProperties() {
  if (typeof window !== 'undefined') {
    const THDCustomer = window.THDCustomer;
    if (THDCustomer && THDCustomer.default
      && typeof (THDCustomer.default.loadCustomerChatConfiguration) === 'function') {
      const data = await THDCustomer.default.loadCustomerChatConfiguration().catch((err) => {
        logger.error(err);
        return false;
      });
      if (data && data.customerConfiguration) {
        const userProperties = {
          userType: data.customerConfiguration.userType ? data.customerConfiguration.userType : 'default',
          token: THDCustomer.default.jwt,
          callCenterType: data.customerConfiguration.callCenterType || ''
        };
        return userProperties;

      }
    }
  }
  return null;
}

/**
 * Builds the conversation context sent to Sprinklr on createConversation.
 *
 * @param {Object} [session] - The assistant's session (the `maa-session` record,
 *   owned by platform/session). Passed in rather than read here: this adapter is a
 *   leaf and the session has exactly one owner (Seq B.3).
 * @param {'app'|'mobile'|'desktop'} [deviceType] - The surface the escalation came
 *   from, derived by getDeviceType from the experience channel at the root.
 */
export async function getSprinklrContext(session, deviceType) {

  const sprinklrAppID = SPRINKLR_CONFIG.APP_ID;
  let sprId = '';

  // Picking up Sprinklr ID from local storage
  if (typeof window.localStorage !== 'undefined') {

    let storageObject = localStorage.getItem('spr-chat-token-' + sprinklrAppID);
    if (storageObject) {
      let payloadEncoded = storageObject.split('.')[1];
      let decodeBase64Url = (str) => {
        let newString = str.replace(/-/g, '+').replace(/_/g, '/');
        while (newString.length % 4) newString += '=';
        return atob(newString);
      };

      if (payloadEncoded) {
        let decodedPayload = decodeBase64Url(payloadEncoded);
        let payload = JSON.parse(decodedPayload);
        let visitSessionId = (payload && payload.visitSessionId ? payload.visitSessionId : '');
        sprId = visitSessionId;
      }

    }

  }

  let ddo = (window.digitalData ? window.digitalData : null);
  const userProperties = await getUserProperties();
  /** @type {{ appId: string, userContext: Record<string, string[]> }} */
  const sprChatSettings = {
    '_c_65b7dde5a5886b114782ce41': [document.URL],
    '_c_65b7d8baa5886b11477f402c': ['b2c'],
    '_c_65b7dfd7a5886b1147844c8f': [''],
    '_c_65b7dda3a5886b1147829dbd': [''],
    '_c_65b7dfaba5886b1147842a6f': [''],
    '_c_60c24dd31f3023769c37fbb0': [''],
    '_c_66d7535fc17bfe53116a80af': [''],
    '_c_66e9e08d099f5a1b6a2615b9': [''],
    '_c_66e9e2ac6723725986d7d5f3': [''],
    '_c_66ec5d4075ae1f33be32d88d': [getSvocId() || ''],
    '_c_66ec5df375ae1f33be331eb1': [userProperties && userProperties?.token ? userProperties.token : ''],
    '_c_65bbbbcf69b8d7310003141b': [userProperties?.callCenterType || ''],
    '_c_67be3f087f15d044e08df095': [sprId || ''],
    '_c_6a3bec3a9579a62e10b0f5d5': [getMcvisID() || ''],
    '_c_6a3bea9f9579a62e10ada1a8': [session?.sessionId || ''],
    '_c_6a3bec179579a62e10b0b0dd': [getSvocId() || ''],
    '_c_6a7f367678f8cf0490e3d314': [deviceType || '']
  };

  if (ddo) {
    const subCategory2 = ddo.page.category?.subCategory2 ? ddo.page.category.subCategory2.toLowerCase() : null;
    const subCategory1 = ddo.page.category?.subCategory1 ? ddo.page.category.subCategory1.toLowerCase() : null;
    const primaryCategory = ddo.page.category?.primaryCategory
      ? ddo.page.category.primaryCategory.toLowerCase() : null;
    const pageCategory = subCategory2 || subCategory1 || primaryCategory || '';

    Object.assign(sprChatSettings, {
      '_c_65b7d8baa5886b11477f402c': [ddo.site.businessType ? ddo.site.businessType.toString().toLowerCase() : 'b2c'],
      '_c_65b7dfd7a5886b1147844c8f': [ddo.page.pageInfo?.pageName ? ddo.page.pageInfo.pageName.toLowerCase() : ''],
      '_c_65b7dda3a5886b1147829dbd': [ddo.page.pageInfo?.pageType ? ddo.page.pageInfo.pageType.toLowerCase() : ''],
      '_c_65b7dfaba5886b1147842a6f': [pageCategory],
      '_c_60c24dd31f3023769c37fbb0': [ddo.user.localization?.storeNumber ? ddo.user.localization.storeNumber : ''],
      '_c_66d7535fc17bfe53116a80af': [ddo.user.localization?.userZip ? ddo.user.localization.userZip : ''],
      '_c_66e9e08d099f5a1b6a2615b9': [ddo.user.localization?.storeZip ? ddo.user.localization.storeZip : ''],
      '_c_66e9e2ac6723725986d7d5f3': [
        ddo.product
        && Array.isArray(ddo.product)
        && ddo.product[0]
        && ddo.product[0]?.productInfo?.sku
        && ddo.product[0]?.productInfo?.sku
          ? ddo.product[0].productInfo.sku : ''
      ],
    });
  }

  logger.log('Derived Sprinklr context settings:', sprChatSettings);
  return sprChatSettings;
}
